import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    makeStyles,
    MenuItem,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import {
    CompanyDocument,
    OrganizationDocumentType,
} from '../../models/Company/types'
import { UpdateCompanyDocumentInput } from '../../hooks/useCompanyDocuments'

const GENERAL_DOCUMENT_TYPE = ''

interface DocumentEditModalProps {
    isOpen: boolean
    onClose: () => void
    document: CompanyDocument | null
    documentTypes: OrganizationDocumentType[]
    onSave: (documentId: number, data: UpdateCompanyDocumentInput) => void
    isSaving: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(1.5),
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    iconContainer: {
        height: 44,
        width: 44,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.light + '20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 600,
    },
    content: {
        padding: theme.spacing(3),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    actions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
}))

export const DocumentEditModal: React.FC<DocumentEditModalProps> = ({
    isOpen,
    onClose,
    document,
    documentTypes,
    onSave,
    isSaving,
}) => {
    const classes = useStyles()

    const [name, setName] = useState('')
    const [documentTypeId, setDocumentTypeId] = useState<string>(
        GENERAL_DOCUMENT_TYPE,
    )
    const [aiSummary, setAiSummary] = useState('')

    useEffect(() => {
        if (!document) return
        setName(document.name || '')
        setDocumentTypeId(
            document.document_type != null
                ? String(document.document_type)
                : GENERAL_DOCUMENT_TYPE,
        )
        setAiSummary(document.ai_summary || '')
    }, [document])

    const handleSave = () => {
        if (!document) return
        const trimmedName = name.trim()
        if (!trimmedName) return

        const data: UpdateCompanyDocumentInput = {
            name: trimmedName,
            document_type:
                documentTypeId === GENERAL_DOCUMENT_TYPE
                    ? null
                    : Number(documentTypeId),
            ai_summary: aiSummary.trim() || null,
        }

        onSave(document.id, data)
    }

    if (!document) return null

    const activeDocumentTypes = documentTypes.filter((t) => t.is_active)

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.paper }}
        >
            <Box className={classes.header}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.iconContainer}>
                        <DescriptionIcon />
                    </Box>
                    <Box>
                        <Typography className={classes.title}>
                            Edit Document
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Update display name, type, and summary
                        </Typography>
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={isSaving}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent className={classes.content}>
                <Box className={classes.form}>
                    <TextField
                        label="Display name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isSaving}
                        helperText="Shown to tenants and in document lists"
                    />

                    <TextField
                        label="Document type"
                        variant="outlined"
                        size="small"
                        fullWidth
                        select
                        value={documentTypeId}
                        onChange={(e) => setDocumentTypeId(e.target.value)}
                        disabled={isSaving}
                    >
                        <MenuItem value={GENERAL_DOCUMENT_TYPE}>
                            General
                        </MenuItem>
                        {activeDocumentTypes.map((type) => (
                            <MenuItem key={type.id} value={String(type.id)}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Summary"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={4}
                        value={aiSummary}
                        onChange={(e) => setAiSummary(e.target.value)}
                        disabled={isSaving}
                        placeholder="Optional summary for internal reference"
                    />
                </Box>
            </DialogContent>

            <DialogActions className={classes.actions}>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving || !name.trim()}
                >
                    {isSaving ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Save'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
